import { Link } from "@tanstack/react-router";

import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { Button } from "../ui/button";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "../settings/settingsLayout";

export interface ProjectBoardPickerProps {
  readonly groupKey: string;
  readonly cmmdProjectIds: readonly string[];
}

/**
 * Shown when a repository has more than one CMMD board.
 *
 * Several boards on one repo is the designed case — CMMD's schema is explicitly
 * many-to-many — so this is a normal destination rather than a disambiguation
 * error, and it says nothing about anything being misconfigured.
 */
export function ProjectBoardPicker(props: ProjectBoardPickerProps) {
  const intake = useCmmdWorkIntakeSnapshot();
  const boards = props.cmmdProjectIds.flatMap((id) => {
    const board = intake.projects.find((candidate) => candidate.id === id);
    return board ? [board] : [];
  });

  return (
    <SettingsPageContainer>
      <SettingsSection title="Boards">
        {boards.map((board) => (
          <SettingsRow
            key={board.id}
            title={board.shortName ?? board.title}
            description={board.title}
            control={
              // A Button rather than a bare link: this is the row's action, and
              // the inline link it replaced rendered at 20px — under half the
              // touch minimum. RESPONSIVE_TEXT_BUTTON_CLASS is what makes that
              // a literal 48px box below `md` for every pointer type, not just
              // coarse ones. Measured at 320px in the browser test beside this.
              <Button
                className={RESPONSIVE_TEXT_BUTTON_CLASS}
                size="sm"
                variant="outline"
                render={
                  <Link
                    to="/project/$projectKey/tasks/$cmmdProjectId"
                    params={{ projectKey: props.groupKey, cmmdProjectId: board.id }}
                  />
                }
              >
                Open
              </Button>
            }
          />
        ))}
      </SettingsSection>
    </SettingsPageContainer>
  );
}
